package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.ChangeStatusRequest
import com.solevo.dist.application.models.Notification
import com.solevo.dist.application.models.NotificationKey
import com.solevo.dist.application.models.NotificationStatus
import com.solevo.dist.application.models.NotificationType
import com.solevo.dist.application.repositories.notifications.NotificationsRepository
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.datetime.*
import org.koin.mp.KoinPlatform.getKoin
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.flow.firstOrNull

class NotificationsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val notificationsRepository: NotificationsRepository = getKoin().get()

    private val _state = MutableStateFlow(NotificationsUiState())
    val state: StateFlow<NotificationsUiState> = _state.asStateFlow()

    private var allNotifications: List<Notification> = emptyList()
    private var currentJob: Job? = null
    private val page = 30
    private var limit = page

    fun start() {
        console.log("[NotificationsViewModel] start() called")
        // If we already have notifications and we're not loading, just filter them
        if (_state.value.loading.not() && allNotifications.isNotEmpty()) {
            console.log("[NotificationsViewModel] Already have notifications, filtering...")
            filterNotifications()
            return
        }
        
        // If already loading, don't start again
        if (_state.value.loading) {
            console.log("[NotificationsViewModel] Already loading, skipping...")
            return
        }
        
        console.log("[NotificationsViewModel] Setting loading = true")
        _state.value = _state.value.copy(loading = true, error = null)

        currentJob?.cancel()
        currentJob = scope.launch {
            try {
                console.log("[NotificationsViewModel] Starting flow collection...")
                
                // First, try to get initial data with timeout
                val initialDataJob = async {
                    try {
                        notificationsRepository.getNotifications(limit, null)
                            .firstOrNull()
                    } catch (e: CancellationException) {
                        console.log("[NotificationsViewModel] Initial data fetch cancelled")
                        throw e
                    } catch (e: Exception) {
                        console.error("[NotificationsViewModel] Error getting initial data:", e)
                        emptyList()
                    }
                }
                
                // Wait for initial data with timeout
                val initialNotifications = try {
                    withTimeoutOrNull(3000) {
                        initialDataJob.await()
                    } ?: emptyList()
                } catch (e: CancellationException) {
                    console.log("[NotificationsViewModel] Initial data fetch cancelled")
                    emptyList()
                }
                
                console.log("[NotificationsViewModel] Got initial data: ${initialNotifications.size} notifications")
                
                // Process initial data
                if (initialNotifications.isNotEmpty()) {
                    val sorted = initialNotifications.sortedByDescending { it.timestamp }
                    allNotifications = sorted
                    console.log("[NotificationsViewModel] Calling computeAndEmit() with initial data")
                    computeAndEmit()
                } else {
                    console.log("[NotificationsViewModel] No initial data, setting loading = false")
                    _state.value = _state.value.copy(
                        loading = false,
                        notifications = emptyMap()
                    )
                }
                
                // Then continue listening for updates
                notificationsRepository.getNotifications(limit, null)
                    .onEach { notifications ->
                        console.log("[NotificationsViewModel] Flow onEach - received ${notifications.size} notifications")
                        val sorted = notifications.sortedByDescending { it.timestamp }
                        allNotifications = sorted
                        console.log("[NotificationsViewModel] Calling computeAndEmit() with ${allNotifications.size} notifications")
                        computeAndEmit()
                    }
                    .catch { t ->
                        if (t !is CancellationException) {
                            console.error("[NotificationsViewModel] Flow error:", t)
                            _state.value = _state.value.copy(
                                loading = false,
                                isLoading = false,
                                error = t.message ?: "Unknown error",
                                notifications = emptyMap()
                            )
                        }
                    }
                    .collect()
            } catch (e: CancellationException) {
                console.log("[NotificationsViewModel] Job cancelled (normal when closing panel)")
                // Don't update state on cancellation
            } catch (e: Exception) {
                console.error("[NotificationsViewModel] Exception in start():", e)
                _state.value = _state.value.copy(
                    loading = false,
                    isLoading = false,
                    error = e.message ?: "Unknown error",
                    notifications = emptyMap()
                )
            }
        }
    }

    fun stop() {
        currentJob?.cancel()
        currentJob = null
    }

    fun selectTab(tab: NotificationsTab) {
        console.log("[NotificationsViewModel] selectTab() called with tab: ${tab.label}")
        if (_state.value.selectedTab == tab) {
            console.log("[NotificationsViewModel] Tab already selected, returning")
            return
        }
        _state.value = _state.value.copy(selectedTab = tab)
        filterNotifications()
    }

    fun loadMore() {
        val currentState = _state.value
        if (!currentState.endReached && !currentState.loading) {
            limit += page
            loadItems()
        }
    }

    private fun loadItems() {
        val currentState = _state.value
        val type = currentState.selectedTab.type
        
        currentJob?.cancel()
        _state.value = currentState.copy(isLoading = true)
        
        currentJob = scope.launch {
            notificationsRepository.getNotifications(limit, type)
                .onEach { notifications ->
                    val sorted = notifications.sortedByDescending { it.timestamp }
                    
                    if (type == null) {
                        // Save notifications for local filtering only if it is selected tab ALL
                        allNotifications = sorted
                    } else {
                        // Update existing notifications with new data
                        val notificationsArray = allNotifications.toMutableList()
                        sorted.forEach { notification ->
                            val index = notificationsArray.indexOfFirst { 
                                it.notificationId == notification.notificationId 
                            }
                            if (index > -1) {
                                notificationsArray[index] = notification
                            }
                        }
                        allNotifications = notificationsArray.toList()
                    }
                    
                    computeAndEmit()
                }
                .catch { t ->
                    _state.value = _state.value.copy(
                        loading = false,
                        isLoading = false,
                        error = t.message ?: "Unknown error"
                    )
                }
                .collect()
        }
    }

    private fun filterNotifications() {
        val selectedTab = _state.value.selectedTab
        var filteredNotifications = allNotifications
        
        when (selectedTab) {
            NotificationsTab.Promotion -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.Promotion 
                }
            }
            NotificationsTab.Orders -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.Order 
                }
            }
            NotificationsTab.Quotes -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.Quote 
                }
            }
            NotificationsTab.LoyaltyTransaction -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.LoyaltyTransaction 
                }
            }
            NotificationsTab.All -> {
                // No filtering needed
            }
        }

        val notificationMap = filteredNotifications
            .groupBy {
                val instant = it.timestamp ?: Clock.System.now()
                instant.toLocalDateTime(TimeZone.currentSystemDefault())
                    .date
                    .atStartOfDayIn(TimeZone.currentSystemDefault())
            }
            .mapKeys { it.key.toLocalDateTime(TimeZone.currentSystemDefault()) }

        val endReached = filteredNotifications.size < limit
        
        _state.value = _state.value.copy(
            notifications = notificationMap,
            endReached = endReached,
            isLoading = false,
            loading = false
        )
    }

    private fun computeAndEmit() {
        console.log("[NotificationsViewModel] computeAndEmit() called")
        val selectedTab = _state.value.selectedTab
        var filteredNotifications = allNotifications
        
        console.log("[NotificationsViewModel] Selected tab: ${selectedTab.label}, allNotifications size: ${allNotifications.size}")
        
        when (selectedTab) {
            NotificationsTab.Promotion -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.Promotion 
                }
            }
            NotificationsTab.Orders -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.Order 
                }
            }
            NotificationsTab.Quotes -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.Quote 
                }
            }
            NotificationsTab.LoyaltyTransaction -> {
                filteredNotifications = allNotifications.filter { 
                    it.classification == NotificationType.LoyaltyTransaction 
                }
            }
            NotificationsTab.All -> {
                // No filtering needed
            }
        }

        console.log("[NotificationsViewModel] Filtered notifications size: ${filteredNotifications.size}")

        val notificationMap = filteredNotifications
            .groupBy {
                val instant = it.timestamp ?: Clock.System.now()
                instant.toLocalDateTime(TimeZone.currentSystemDefault())
                    .date
                    .atStartOfDayIn(TimeZone.currentSystemDefault())
            }
            .mapKeys { it.key.toLocalDateTime(TimeZone.currentSystemDefault()) }

        val endReached = filteredNotifications.size < limit
        
        console.log("[NotificationsViewModel] Setting loading = false, notifications map size: ${notificationMap.size}")
        _state.value = _state.value.copy(
            loading = false,
            isLoading = false,
            error = null,
            notifications = notificationMap,
            endReached = endReached
        )
        console.log("[NotificationsViewModel] State updated, loading = ${_state.value.loading}")
    }

    fun removeNotification(date: LocalDateTime, notificationId: String) {
        val currentState = _state.value
        scope.launch {
            try {
                notificationsRepository.changeNotificationStatus(
                    ChangeStatusRequest(
                        NotificationKey(notificationId),
                        NotificationStatus.Deleted.name
                    )
                )
                
                val mapNotifications = currentState.notifications.toMutableMap()
                mapNotifications[date] = mapNotifications[date]
                    ?.filter { it.notificationId != notificationId }
                    .orEmpty()
                
                if (mapNotifications[date].isNullOrEmpty()) {
                    mapNotifications.remove(date)
                }
                
                // Also remove from allNotifications
                allNotifications = allNotifications.filter { 
                    it.notificationId != notificationId 
                }
                
                _state.value = currentState.copy(
                    notifications = mapNotifications,
                    error = null
                )
            } catch (e: Exception) {
                _state.value = currentState.copy(
                    error = e.message ?: "Failed to delete notification"
                )
            }
        }
    }

    fun updateRevealedItem(notificationId: String?) {
        _state.value = _state.value.copy(
            revealedNotification = notificationId,
            error = null
        )
    }
}

/* ------------ UI state / models ------------ */

enum class NotificationsTab(val label: String) {
    All("All"),
    Orders("Orders"),
    Quotes("Quotes"),
    Promotion("Promotions"),
    LoyaltyTransaction("Loyalty Transactions");

    val type: NotificationType?
        get() = when (this) {
            All -> null
            Orders -> NotificationType.Order
            Quotes -> NotificationType.Quote
            Promotion -> NotificationType.Promotion
            LoyaltyTransaction -> NotificationType.LoyaltyTransaction
        }
}

data class NotificationsUiState(
    val loading: Boolean = false,
    val notifications: Map<LocalDateTime, List<Notification>> = emptyMap(),
    val selectedTab: NotificationsTab = NotificationsTab.All,
    val endReached: Boolean = false,
    val isLoading: Boolean = false,
    val revealedNotification: String? = null,
    val error: String? = null
)

